/**
 * ImpersonatePanel — admin-only "Act as user" entry point.
 *
 * Lives inside AccountDetailView, ABOVE the StatusTogglePanel. The
 * disabled-with-tooltip matrix matches Build Plan §10:
 *   - target is an admin             → "Cannot impersonate other admins"
 *   - target is the current admin    → "Cannot impersonate yourself"
 *   - admin already impersonating    → "Already impersonating — exit
 *                                       current session first"
 *
 * Flow:
 *   1. Click "Act as user" → opens the reason modal.
 *   2. Type 4–200 char reason → Submit enables.
 *   3. Submit → POST /api/admin/impersonation/start → refreshMe →
 *      navigate to /dashboard/customer (or /dashboard/provider) per
 *      the backend's `redirect_to`.
 *
 * Wrapped in TooltipProvider locally because AccountDetailView's tree
 * doesn't have one at the top — adding it here keeps the boundary
 * tight.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Loader2, UserCog } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const REASON_MIN = 4;
const REASON_MAX = 200;

export default function ImpersonatePanel({ detail }) {
  const { user: currentAdmin, refreshMe } = useAuth();
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Disabled-state matrix (priority order matters — first hit wins).
  const disabledReason = useMemo(() => {
    if (!detail || !currentAdmin) return null;
    if (detail.id === currentAdmin.id) return "Cannot impersonate yourself";
    if (detail.is_admin) return "Cannot impersonate other admins";
    if (currentAdmin.is_impersonating) {
      return "Already impersonating — exit current session first";
    }
    return null;
  }, [detail, currentAdmin]);

  const isDisabled = !!disabledReason;

  const reasonTrimmed = reason.trim();
  const reasonError = useMemo(() => {
    if (reasonTrimmed.length === 0) return `Required (min ${REASON_MIN}).`;
    if (reasonTrimmed.length < REASON_MIN)
      return `Need at least ${REASON_MIN} characters.`;
    if (reasonTrimmed.length > REASON_MAX)
      return `${REASON_MAX} characters max.`;
    return null;
  }, [reasonTrimmed]);
  const submitEnabled = !reasonError && !submitting;

  const openModal = () => {
    if (isDisabled) return;
    setReason("");
    setModalOpen(true);
  };

  const submit = async () => {
    if (!submitEnabled || !detail) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/impersonation/start", {
        target_user_id: detail.id,
        reason: reasonTrimmed,
      });
      await refreshMe();
      toast.success(`Now acting as ${data.session.target_email}`);
      setModalOpen(false);
      // Backend chooses the right dashboard (customer vs provider)
      // based on the target's primary role.
      const redirect = data.redirect_to || "/";
      navigate(redirect);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section data-testid="impersonate-panel" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="font-heading text-lg font-semibold text-pp-text">
              Act as this user
            </h4>
            <p className="mt-1 text-sm text-pp-text-muted">
              Start a 1-hour, fully audited session to view + act on the
              platform as this user. Required for support work that
              touches their bookings, dogs, or payouts.
            </p>
          </div>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* span wrapper lets the tooltip target a disabled
                    button (Radix won't fire on a truly disabled
                    element). The button still has aria-disabled +
                    onClick guard for keyboard users. */}
                <span className="inline-block">
                  <Button
                    type="button"
                    onClick={openModal}
                    disabled={isDisabled}
                    aria-disabled={isDisabled}
                    className="h-10 rounded-full bg-red-600 px-5 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                    data-testid="impersonate-panel-btn"
                  >
                    <UserCog className="mr-2 h-4 w-4" />
                    Act as user
                  </Button>
                </span>
              </TooltipTrigger>
              {isDisabled && (
                <TooltipContent
                  side="top"
                  align="end"
                  data-testid="impersonate-panel-disabled-tip"
                >
                  <p className="text-xs">{disabledReason}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </section>

      {/* Reason modal */}
      <AlertDialog open={modalOpen} onOpenChange={setModalOpen}>
        <AlertDialogContent data-testid="impersonate-reason-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Act as {detail?.name || detail?.email}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Every action you take during this session will be recorded
              against your admin account. Sessions auto-expire after
              1 hour. Provide a short reason so the audit trail has
              context.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2">
            <Label
              htmlFor="impersonate-reason-input"
              className="text-xs font-semibold uppercase tracking-wider text-pp-text-muted"
            >
              Reason ({REASON_MIN}–{REASON_MAX} characters)
            </Label>
            <Textarea
              id="impersonate-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Investigating booking payment failure ref #1234"
              rows={4}
              maxLength={REASON_MAX + 50}
              data-testid="impersonate-reason-input"
              autoFocus
            />
            <div className="flex items-center justify-between text-[11px]">
              <span
                className={
                  reasonError
                    ? "font-semibold text-red-600"
                    : "text-pp-text-muted"
                }
              >
                {reasonError || "Looks good."}
              </span>
              <span
                className="text-pp-text-muted"
                data-testid="impersonate-reason-counter"
              >
                {reasonTrimmed.length}/{REASON_MAX}
              </span>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="impersonate-cancel-btn"
              disabled={submitting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                submit();
              }}
              disabled={!submitEnabled}
              className="bg-red-600 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="impersonate-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Starting…
                </>
              ) : (
                "Start session"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
